import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";

/**
 * LocationRelatedSection — "Gerelateerde diensten en regio's" sectie
 * onderaan elke locatiepagina. Voegt interne links toe voor SEO authority
 * distribution zonder bestaande content/styling aan te passen.
 *
 * Gebruikt bestaande tokens: #0F0F23 cards, #00D4FF accent, border-white/10.
 */

// City labels for visible link text. Keys are the URL slug used by the routes.
const CITY_LABEL = {
  almere: "Almere",
  amsterdam: "Amsterdam",
  "den-haag": "Den Haag",
  rotterdam: "Rotterdam",
  utrecht: "Utrecht",
  waddinxveen: "Waddinxveen",
  "alphen-aan-den-rijn": "Alphen aan den Rijn",
  alkmaar: "Alkmaar",
  barneveld: "Barneveld",
  breda: "Breda",
  amersfoort: "Amersfoort",
  zeewolde: "Zeewolde",
};

// Nearby region mapping per current-city slug. 3-5 entries each.
// User-specified mappings preserved exactly; remaining cities mapped by
// geographic proximity within the same/adjacent provinces.
const NEARBY = {
  // User-specified
  breda: ["waddinxveen", "rotterdam", "den-haag", "alphen-aan-den-rijn"],
  amersfoort: ["utrecht", "barneveld", "zeewolde", "almere"],
  alkmaar: ["amsterdam", "alphen-aan-den-rijn", "utrecht"],
  zeewolde: ["almere", "amersfoort", "barneveld", "utrecht"],
  waddinxveen: ["breda", "rotterdam", "den-haag", "alphen-aan-den-rijn"],
  "alphen-aan-den-rijn": ["waddinxveen", "amsterdam", "den-haag", "breda"],
  // Filled in by geographic adjacency
  almere: ["zeewolde", "amsterdam", "amersfoort", "utrecht"],
  amsterdam: ["alkmaar", "almere", "den-haag", "utrecht"],
  "den-haag": ["rotterdam", "waddinxveen", "alphen-aan-den-rijn", "breda"],
  rotterdam: ["den-haag", "waddinxveen", "breda", "alphen-aan-den-rijn"],
  utrecht: ["amersfoort", "almere", "amsterdam", "barneveld"],
  barneveld: ["amersfoort", "utrecht", "zeewolde", "almere"],
};

const SERVICES_NL = [
  {
    to: "/mobiele-truckwash",
    anchor: "Lees meer over onze mobiele truckwash service",
    note: "Vrachtwagens en bedrijfsvloten gewassen op uw eigen terrein.",
  },
  {
    to: "/mobiele-truckwash-nederland",
    anchor: "Mobiele truckwash in heel Nederland",
    note: "Landelijke dekking vanuit Almere en Den Haag — geen omrijden, geen stilstand.",
  },
  {
    to: "/wagenpark-reiniging",
    anchor: "Bekijk onze fleet cleaning aanpak voor wagenparken",
    note: "Vaste cyclus, structureel onderhoud, één digitaal dossier per kenteken.",
  },
  {
    to: "/wagenparkreiniging",
    anchor: "Professionele wagenparkreiniging — fleet cleaning op locatie",
    note: "Structureel onderhoud, vaste operators en transparante facturatie per wasdag.",
  },
  {
    to: "/haccp-certificaten",
    anchor: "Bekijk onze HACCP- en certificeringsoplossingen",
    note: "HACCP-werkwijze voor interieurreiniging met digitale reinigingsregistratie.",
  },
  {
    to: "/waszbase",
    anchor: "Bekijk hoe WaszBase wagenparkbeheer digitaliseert",
    note: "Realtime status, certificaten en facturatie per wasdag in één portaal.",
  },
  {
    to: "/digitale-wagenparkadministratie",
    anchor: "Digitale wagenparkadministratie via WaszBase",
    note: "Reinigingsregistratie, certificaten en kentekendossier op één plek.",
  },
  {
    to: "/reinigingsregistratie-transport",
    anchor: "Digitale reinigingsregistratie voor transport",
    note: "Bewijslast per kenteken, NVWA-klaar, direct exporteerbaar.",
  },
  {
    to: "/certificaatbeheer-wagenpark",
    anchor: "Certificaatbeheer voor uw wagenpark",
    note: "Reinigingscertificaten en HACCP-documentatie centraal in WaszBase.",
  },
  {
    to: "/over-ons",
    anchor: "Maak kennis met het team achter Wasz.nl",
    note: "Onze achtergrond, werkwijze en visie op mobiele truckwash.",
  },
  {
    to: "/blog",
    anchor: "Lees onze kennisbank en praktijkcases",
    note: "Praktijkverhalen, compliance-tips en operationele inzichten.",
  },
];

const SERVICES_EN = [
  {
    to: "/mobiele-truckwash",
    anchor: "Learn more about our mobile truckwash service",
    note: "Trucks and corporate fleets washed at your own site.",
  },
  {
    to: "/mobiele-truckwash-nederland",
    anchor: "Mobile truckwash across the Netherlands",
    note: "Nationwide coverage from Almere and The Hague — no detours, no downtime.",
  },
  {
    to: "/wagenpark-reiniging",
    anchor: "Explore our fleet cleaning approach",
    note: "Fixed cycle, structural maintenance, one digital dossier per licence plate.",
  },
  {
    to: "/wagenparkreiniging",
    anchor: "Professional fleet cleaning on site",
    note: "Structured maintenance, fixed operators and transparent per-wash-day billing.",
  },
  {
    to: "/haccp-certificaten",
    anchor: "See our HACCP and certification solutions",
    note: "HACCP working methods for interior cleaning with digital cleaning records.",
  },
  {
    to: "/waszbase",
    anchor: "See how WaszBase digitises fleet management",
    note: "Real-time status, certificates and per-wash-day billing in one portal.",
  },
  {
    to: "/digitale-wagenparkadministratie",
    anchor: "Digital fleet administration via WaszBase",
    note: "Cleaning records, certificates and per-licence-plate dossier in one place.",
  },
  {
    to: "/reinigingsregistratie-transport",
    anchor: "Digital cleaning records for transport",
    note: "Evidence per licence plate, NVWA-ready, instantly exportable.",
  },
  {
    to: "/certificaatbeheer-wagenpark",
    anchor: "Certificate management for your fleet",
    note: "Cleaning certificates and HACCP documentation centrally in WaszBase.",
  },
  {
    to: "/over-ons",
    anchor: "Meet the team behind Wasz.nl",
    note: "Our background, working methods and view on mobile truckwash.",
  },
  {
    to: "/blog",
    anchor: "Read our knowledge base and case studies",
    note: "Real-world stories, compliance tips and operational insights.",
  },
];

const LocationRelatedSection = ({ slug, city, lang = "nl" }) => {
  const services = lang === "en" ? SERVICES_EN : SERVICES_NL;
  const nearby = NEARBY[slug] || [];

  const labels = {
    overline: lang === "nl" ? "Verken verder" : "Explore further",
    title: lang === "nl" ? "Gerelateerde diensten en regio's" : "Related services and regions",
    services: lang === "nl" ? "Diensten" : "Services",
    nearbyHeading:
      lang === "nl" ? "Actief in nabijgelegen regio's" : "Active in nearby regions",
    nearbyLead:
      lang === "nl"
        ? `Werkt u ook in deze regio's rond ${city}? Onze mobiele units bedienen het volledige werkgebied vanuit Almere en Den Haag.`
        : `Operating in the regions around ${city}? Our mobile units serve the full area from Almere and The Hague.`,
  };

  return (
    <section
      data-testid={`location-${slug}-related`}
      className="py-20 sm:py-24 bg-[#0a0a18] border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mb-12">
          <p className="section-overline mb-3">{labels.overline}</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
            {labels.title}
          </h2>
        </div>

        {/* Diensten */}
        <div className="mb-14">
          <h3 className="text-xs uppercase tracking-[0.18em] text-white/45 font-semibold mb-6">
            {labels.services}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                data-testid={`location-${slug}-related-service-${s.to.replace(/^\//, "")}`}
                className="group rounded-xl border border-white/10 bg-[#0F0F23] p-5 card-hover h-full flex flex-col"
              >
                <span className="text-[15px] font-semibold text-white group-hover:text-[#00D4FF] transition-colors">
                  {s.anchor}
                </span>
                <span className="mt-2 text-[13px] text-white/55 leading-relaxed flex-1">
                  {s.note}
                </span>
                <span className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-[#00D4FF]">
                  {lang === "nl" ? "Bekijken" : "View"}
                  <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Nabijgelegen regio's */}
        {nearby.length > 0 && (
          <div>
            <h3 className="text-xs uppercase tracking-[0.18em] text-white/45 font-semibold mb-3">
              {labels.nearbyHeading}
            </h3>
            <p className="text-white/55 text-sm leading-relaxed max-w-2xl mb-6">
              {labels.nearbyLead}
            </p>
            <div className="flex flex-wrap gap-3">
              {nearby.map((s) => {
                const label = CITY_LABEL[s] || s;
                return (
                  <Link
                    key={s}
                    to={`/truckwash-${s}`}
                    data-testid={`location-${slug}-nearby-${s}`}
                    className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#0F0F23] px-4 py-2 text-sm text-white/75 hover:text-white hover:border-[#00D4FF]/40 hover:bg-[#101030] transition-colors"
                  >
                    <MapPin className="w-3.5 h-3.5 text-[#00D4FF]" />
                    <span>Truckwash {label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Interessante artikelen — links naar kennisbank */}
        <div className="mt-14">
          <h3 className="text-xs uppercase tracking-[0.18em] text-white/45 font-semibold mb-6">
            {lang === "nl" ? "Interessante artikelen" : "Related articles"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                to: "/blog/10-jaar-wasz",
                title: lang === "nl"
                  ? "10 jaar Wasz.nl: van mobiele truckwash naar digitaal wagenparkbeheer"
                  : "10 years of Wasz.nl: from mobile truckwash to digital fleet management",
                excerpt: lang === "nl"
                  ? "Wasz.nl bestaat 10 jaar. Lees hoe wij zijn uitgegroeid tot een landelijke partner voor reiniging, compliance en digitaal wagenparkbeheer."
                  : "Wasz.nl turns 10. Read how we grew into a nationwide partner for cleaning, compliance and digital fleet management.",
              },
              {
                to: "/van-demo-tot-digitale-wagenparkcontrole",
                title: lang === "nl"
                  ? "Van Demo tot Digitale Wagenparkcontrole"
                  : "From Demo to Digital Fleet Control",
                excerpt: lang === "nl"
                  ? "Praktijkverhaal van een transportbedrijf dat overstapte op Wasz.nl: drie demovoertuigen, een vaste wekelijkse planning en realtime wagenparkinzicht via WaszBase."
                  : "Real story of a transport operator switching to Wasz.nl: three demo vehicles, a fixed weekly schedule and real-time fleet visibility via WaszBase.",
              },
              {
                to: "/blog/slimmere-planning-minder-stilstand",
                title: lang === "nl"
                  ? "Slimmere planning, minder stilstand"
                  : "Smarter planning, less downtime",
                excerpt: lang === "nl"
                  ? "Hoe vaste wascycli en realtime wagenparkinzicht onnodige stilstand voorkomen — en uw planning rustiger maken."
                  : "How fixed wash cycles and real-time fleet visibility prevent unnecessary downtime — and calm your planning.",
              },
            ].map((a) => (
              <Link
                key={a.to}
                to={a.to}
                data-testid={`location-${slug}-article-${a.to.replace(/[/]/g, "-").replace(/^-/, "")}`}
                className="group rounded-xl border border-white/10 bg-[#0F0F23] p-5 card-hover h-full flex flex-col"
              >
                <span className="text-[11px] uppercase tracking-[0.16em] text-[#00D4FF] font-semibold mb-2">
                  {lang === "nl" ? "Kennisbank" : "Knowledge base"}
                </span>
                <span className="text-[15px] font-semibold text-white group-hover:text-[#00D4FF] transition-colors leading-snug mb-2">
                  {a.title}
                </span>
                <span className="text-[13px] text-white/55 leading-relaxed flex-1">
                  {a.excerpt}
                </span>
                <span className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-[#00D4FF]">
                  {lang === "nl" ? "Lees artikel" : "Read article"}
                  <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationRelatedSection;
